import { ClaimPhysicalCardRequest, Partner, PaymentMethod } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Checkbox from '@platform-ui-common/components/Checkbox/index.js';
import GenericAddressField from '@platform-ui-common/components/GenericAddressField/index.js';
import InfoDisplay from '@platform-ui-common/components/InfoDisplay/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useForm, {
  Action,
  defaultRequiredErrorMessage,
  GenericFormData,
  optionalField,
  requiredField,
  ValidationMode,
} from '@platform-ui-common/hooks/useForm/index.js';
import { formatAddress } from '@platform-ui-common/utilities/format.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import ClaimForm from '@platform-ui/claim/components/ClaimForm/index.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { Dispatch, ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';

const defaultAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  copied: false,
};

const addressContent = (
  partner: Partner,
): Record<
  Extract<
    PaymentMethod,
    PaymentMethod.Check | PaymentMethod.PhysicalCard | PaymentMethod.VirtualCard
  >,
  {
    subHeader: string;
    infoText?: ReactNode;
    overrideAddress?: boolean;
  }
> => ({
  [PaymentMethod.Check]: {
    subHeader:
      'Your mailing address is where we will send your paper check. Please make sure to complete all the relevant fields of your mailing address.',
  },
  [PaymentMethod.PhysicalCard]: {
    subHeader: 'Your mailing address is where your prepaid card will be sent.',
    infoText: `Don’t have a street address? Please pick up your prepaid card from ${
      partner.name
    }’s address, ${formatAddress(partner.mailingAddress)}, in 7 business days.`,
    overrideAddress: true,
  },
  [PaymentMethod.VirtualCard]: {
    subHeader: 'Your mailing address is needed to request a virtual prepaid card.',
    infoText: `Don’t have a street address? Please use ${partner.name}’s address, ${formatAddress(
      partner.mailingAddress,
    )}, when making online purchases. This address can be found in your Akimbo account.`,
    overrideAddress: true,
  },
});

type SupportedMethod = Extract<
  PaymentMethod,
  PaymentMethod.Check | PaymentMethod.PhysicalCard | PaymentMethod.VirtualCard
>;

function MailingAddress(): JSX.Element {
  const { fulfillmentId, payload, setPayload, method } = useClaim<ClaimPhysicalCardRequest>();
  const partner = usePartner();
  const [showManualButton, setShowManualButton] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [isUnhoused, setUnhoused] = useState(false);

  const supportedMethod = (method ?? PaymentMethod.Check) as SupportedMethod;
  const { subHeader, infoText, overrideAddress } = addressContent(partner)[supportedMethod];
  const { formData, dispatch, errors, trySubmit } = useForm(
    {
      addressLine1: payload?.address?.addressLine1 ?? '',
      addressLine2: payload?.address?.addressLine2 ?? '',
      city: payload?.address?.city ?? '',
      state: payload?.address?.state ?? '',
      zip: payload?.address?.zip ?? '',
      copied: false,
    },
    {
      addressLine1: requiredField(undefined, defaultRequiredErrorMessage('Street Address')),
      city: requiredField(undefined, defaultRequiredErrorMessage('City')),
      state: requiredField(({ state }) => {
        if (!partner?.mailingAddress || isUnhoused) return;
        if (state === partner.mailingAddress.state) return;
        return `To receive funds for this program, your mailing address must be in ${partner.mailingAddress.state}.`;
      }, defaultRequiredErrorMessage('State')),
      zip: requiredField(undefined, defaultRequiredErrorMessage('Zip Code')),
      addressLine2: optionalField(),
    },
    ValidationMode.RequiredOnSubmit,
  );
  const history = useHistory();

  const onSubmit = (): void => {
    trySubmit(() => {
      const { copied: _, ...address } = formData;
      setPayload({ ...payload, address });
      history.push(makeRoute(ClaimRoutes.REVIEW, { fulfillmentId }));
    });
  };

  const onOverrideAddress = (value: boolean): void => {
    setUnhoused(value);
    setShowManualEntry(false);
    dispatch({
      type: 'reset',
      payload: value
        ? {
            addressLine1: partner?.mailingAddress?.addressLine1 ?? '',
            addressLine2: partner?.mailingAddress?.addressLine2 ?? '',
            city: partner?.mailingAddress?.city ?? '',
            state: partner?.mailingAddress?.state ?? '',
            zip: partner?.mailingAddress?.zip ?? '',
            copied: true,
          }
        : defaultAddress,
    });
  };

  const errorMessages = Object.values(errors).flat();
  const showManualAddressMessaging = showManualButton && !showManualEntry && !isUnhoused;

  return (
    <ClaimForm
      header="Please enter your mailing address"
      subHeader={subHeader}
      onSubmit={onSubmit}
      errors={errorMessages as string[]}
      helperText={isUnhoused && infoText && <Message variant="info">{infoText}</Message>}
    >
      {infoText && (
        <div className="lg:w-3/4 text-center pb-4">
          <Typography variant="body">* An asterisk indicates a required response</Typography>
        </div>
      )}
      {partner?.mailingAddress && overrideAddress && (
        <div className="lg:w-field text-center">
          <Checkbox
            label={
              <Typography variant="body" tag="span">
                I don’t have a street address
              </Typography>
            }
            checked={isUnhoused}
            onClick={onOverrideAddress}
          />
        </div>
      )}
      <GenericAddressField
        field={{
          validation: { required: true },
          ...(!!partner?.mailingAddress && { filter: { state: partner.mailingAddress.state } }),
        }}
        answers={formData}
        errors={errors}
        dispatch={dispatch as unknown as Dispatch<Action<GenericFormData>>}
        showManualAddressButton={setShowManualButton}
        showManualInput={showManualEntry}
        disabled={false}
      />

      <InfoDisplay
        message="Can't find your address? Click the button below to manually input your address."
        visible={showManualAddressMessaging}
        classNames="lg:w-field mt-4"
      />
      {showManualAddressMessaging && (
        <Button variant="outline" onClick={(): void => setShowManualEntry(!showManualEntry)}>
          Manually input address
        </Button>
      )}
    </ClaimForm>
  );
}

export default MailingAddress;
